import { getData, postData, putData } from "../api/api";
import { parseBackendNotificationDetails } from "../utils/notification";

/** OpenAPI: MailSettingsRequest.provider / MailSettingsResponse.provider */
export type MailProvider = "CUSTOM_SMTP" | "OFFICE365_GRAPH";

/** OpenAPI: MailSettingsResponse */
export type MailSettingsResponse = {
  enabled?: boolean;
  provider?: string;
  host?: string;
  port?: number;
  username?: string;
  passwordSet?: boolean;
  fromAddress?: string;
  startTls?: boolean;
  tenantId?: string;
  clientId?: string;
  clientSecretSet?: boolean;
  allowedRecipientDomains?: string[];
  configured?: boolean;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
};

export type MailSettings = MailSettingsResponse & {
  enabled: boolean;
  provider: MailProvider;
  port: number;
  passwordSet: boolean;
  startTls: boolean;
  clientSecretSet: boolean;
  allowedRecipientDomains: string[];
  configured: boolean;
};

/** OpenAPI: MailSettingsRequest */
export type MailSettingsRequest = {
  enabled?: boolean;
  provider?: string;
  host?: string;
  port?: number;
  username?: string;
  /** Write-only; omit or blank to keep the stored password. */
  password?: string;
  fromAddress?: string;
  startTls?: boolean;
  tenantId?: string;
  clientId?: string;
  /** Write-only; omit or blank to keep the stored client secret. */
  clientSecret?: string;
  allowedRecipientDomains?: string[];
};

export type MailSettingsSavePayload = MailSettingsRequest & {
  enabled: boolean;
  provider: MailProvider;
  port: number;
  startTls: boolean;
  allowedRecipientDomains: string[];
};

/** OpenAPI: MailTestRequest */
export type MailTestRequest = {
  to: string;
  subject?: string;
  body?: string;
};

const BASE = "/admin/mail-settings";
const SKIP_GLOBAL = { skipGlobalErrorHandler: true };

export const EMPTY_MAIL_SETTINGS: MailSettings = {
  enabled: false,
  provider: "CUSTOM_SMTP",
  host: "",
  port: 587,
  username: "",
  passwordSet: false,
  fromAddress: "",
  startTls: true,
  tenantId: "",
  clientId: "",
  clientSecretSet: false,
  allowedRecipientDomains: [],
  configured: false,
};

const isUnconfigured = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 404;
};

export function unwrapMailSettings(data: unknown): MailSettings {
  if (!data || typeof data !== "object") {
    return EMPTY_MAIL_SETTINGS;
  }

  const obj = data as Record<string, unknown>;
  const nested = obj.response;
  const source =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : obj;

  if (!("enabled" in source || "provider" in source || "configured" in source || "host" in source)) {
    return EMPTY_MAIL_SETTINGS;
  }

  const provider = source.provider === "OFFICE365_GRAPH" ? "OFFICE365_GRAPH" : "CUSTOM_SMTP";
  const port = Number(source.port);

  return {
    ...EMPTY_MAIL_SETTINGS,
    ...(source as Partial<MailSettings>),
    enabled: Boolean(source.enabled),
    provider,
    port: Number.isFinite(port) && port > 0 ? port : 587,
    passwordSet: Boolean(source.passwordSet),
    startTls: source.startTls !== false,
    clientSecretSet: Boolean(source.clientSecretSet),
    allowedRecipientDomains: Array.isArray(source.allowedRecipientDomains)
      ? source.allowedRecipientDomains.filter((item): item is string => typeof item === "string")
      : [],
    configured: Boolean(source.configured),
  };
}

export function getMailSettingsApiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const details = parseBackendNotificationDetails(response?.data);
  const parts = [details.message, details.reason, response?.status ? `HTTP ${response.status}` : ""]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean);
  if (parts.length > 0) {
    return parts.join(" — ");
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

export const mailSettingsService = {
  get: async (): Promise<MailSettings> => {
    try {
      return unwrapMailSettings(await getData(BASE, SKIP_GLOBAL));
    } catch (error) {
      if (isUnconfigured(error)) {
        return EMPTY_MAIL_SETTINGS;
      }
      throw error;
    }
  },

  save: async (payload: MailSettingsSavePayload): Promise<MailSettings> =>
    unwrapMailSettings(await putData(BASE, payload, SKIP_GLOBAL)),

  sendTest: (payload: MailTestRequest): Promise<Record<string, unknown>> =>
    postData(`${BASE}/test`, payload, SKIP_GLOBAL) as Promise<Record<string, unknown>>,
};
